import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, Card, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { GenericMuiDatagrid } from "../../../components/generic/GenericDatagrid";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { formatDate } from "../../../helpers/dateHelper";
import { DECLINE, EMAIL_VERIFIED } from "../../../enums/statusEnums";
import { approveUser, getUnverifiedAccounts, } from "../../../config/apisauce";
import { DECLINE_CHECKLIST, INITIAL_GET_PARAMS } from "../../../enums/generalEnums";
import { DAY_ONLY_FORMAT } from "../../../enums/dateEnums";
import { ReasonDialog } from "../dialogs/ReasonDialog";
import useNotiStack from "../../../hooks/useNotiStack";
import LoadingButton from "@mui/lab/LoadingButton";
import { GenericNoData } from "../../../components/generic/GenericNoData";
import { UsersThree, Warning } from "@phosphor-icons/react";
import { ProfileDetails } from "./profileDetails/ProfileDetails";
import { router } from "@inertiajs/react";
import useStore from "../../../states/store";
import { GenericDialog } from "../../../components/generic/GenericDialog";
import { GenericCircleIcon } from "../../../components/generic/GenericCircleIcon";
import { kebabToSentence } from "../../../helpers/stringHelper";

export const UnverifiedRejectedAccounts = ({ reloadStats, isHomePage = true }) => {
  const { success } = useNotiStack();
  const [, { openLoader, closeLoader }] = useStore();
  const [unverifiedAccounts, setUnverifiedAccounts] = useState({});
  const [openReasonDialog, setOpenReasonDialog] = useState(false);
  const [openProfileDetailsDialog, setOpenProfileDetailsDialog] = useState(false);
  const [dialogDetails, setDialogDetails] = useState({});
  const [details, setDetails] = useState('');
  const [stillProcessing, setStillProcessing] = useState([]);
  const [userId, setUserId] = useState(null);
  const [pagination, setPagination] = useState({ pageSize: isHomePage ? 5 : 10, page: 0 });
  const [acceptConfirmationState, setAcceptConfirmationState] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const genericColumnProps = {
    editable: false,
    sortable: false,
    resizable: false
  };

  const handleOpenProfileDetailsDialog = (id) => {
    setUserId(id);
    setOpenProfileDetailsDialog(true);
  };

  const handleCloseProfileDetailsDialog = () => {
    setUserId(null);
    setOpenProfileDetailsDialog(false);
  };

  const handleOpenDialog = (label, data, checklists, action) => {
    setDialogDetails({ label: label, data: data, checklists: checklists, action: action });
    setOpenReasonDialog(true);
    setOpenProfileDetailsDialog(false);
  };

  const handleCloseReasonDialog = () => {
    setOpenReasonDialog(false);
    setDialogDetails({});
  };

  const handleRemoveFromList = (id) => {
    setUnverifiedAccounts((prev) => {
      const currentList = { ...prev };
      currentList.data = currentList.data?.filter((item) => item?.id !== id);
      return currentList;
    });
    isHomePage && reloadStats();
  };

  const handleAcceptUser = async (values) => {
    setStillProcessing((prev) => ([...prev, values?.id]));
    const params = {
      user_id: values?.id,
      email: values?.email,
      name: values?.name,
    };
    const { ok, data } = await approveUser(params);
    if (ok) {
      handleRemoveFromList(data?.id);
      success('User has been accepted!');
    }
    setAcceptConfirmationState(false);
    setStillProcessing((prev) => prev?.filter(item => item !== values?.id));
  };

  const handleAcceptChecker = (values) => {
    setAcceptConfirmationState(true);
    setSelectedCreator(values);
  };

  const fetchUnverifiedAccounts = async () => {
    const params = {
      ...INITIAL_GET_PARAMS,
      per_page: pagination?.pageSize,
      page: pagination?.page + 1
    };
    openLoader();
    const response = await getUnverifiedAccounts(params);
    if (response.ok) {
      setUnverifiedAccounts(response?.data);
    }
    closeLoader();
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      renderCell: ({ row }) => {
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1.5} mt={0.5}>
            <GenericAvatar location={row?.profile_picture_location} avatarId={row?.id} size={'42px'} backgroundColor={'background.default'} />
            {row?.name ? <Typography fontWeight={700}>{row?.name}</Typography> : <Typography color="grey.light">N/A</Typography>}
          </Stack>
        );
      },
      ...genericColumnProps
    },
    {
      field: 'profession',
      headerName: 'Profession',
      flex: 1,
      renderCell: ({ row }) => row?.profession ?? <Typography mt={1.7} color="grey.light">N/A</Typography>,
      ...genericColumnProps
    },
    {
      field: 'created_at',
      headerName: 'Request Date',
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...genericColumnProps
    },
    {
      field: 'referral_code',
      headerName: 'Referral Code',
      flex: 1,
      renderCell: ({ row }) => JSON.parse(row?.meta)?.reffered_by ?? <Typography mt={1.7} color="grey.light">N/A</Typography>,
      ...genericColumnProps
    },
    {
      field: 'current_step',
      headerName: 'Current Step',
      flex: 1,
      renderCell: ({ row }) => row?.current_step ? <Typography mt={1.7} textTransform={'capitalize'} > {kebabToSentence(row?.current_step)}</Typography> : 'Initial Signup',
      ...genericColumnProps
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography
            mt={1.7}
            fontWeight={700}
            textTransform={'capitalize'}
            color={'error'}
          >
            {row?.status === EMAIL_VERIFIED ? 'Ondato Unverified' : 'Email Unverified'}
          </Typography>
        );
      },
      ...genericColumnProps
    },
    {
      field: 'details',
      headerName: 'Details',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack gap={1} direction={'row'} mt={1.1}>
            <Button fullWidth variant="contained" sx={{ color: '#FFF' }} onClick={() => handleOpenProfileDetailsDialog(row?.id)}>Details</Button>
          </Stack>
        );
      },
      ...genericColumnProps
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1.4,
      renderCell: ({ row }) => {
        const processing = stillProcessing?.includes(row?.id);
        return (
          <Stack gap={1} direction={'row'} mt={1.1}>
            <Button disabled={processing} onClick={() => handleOpenDialog('Declining', row, DECLINE_CHECKLIST, DECLINE)} fullWidth variant="contained" color="error" sx={{ color: '#FFF' }}>Decline</Button>
            {row.current_step !== null &&
              <LoadingButton
                fullWidth
                onClick={() => handleAcceptChecker(row)}
                size="small"
                loading={processing}
                variant="contained"
                disabled={processing}
                color={'warning'}
              >
                Accept
              </LoadingButton>
            }
          </Stack>
        );
      },
      ...genericColumnProps
    }
  ];

  useEffect(() => {
    fetchUnverifiedAccounts();
  }, [pagination]);

  return (
    <Fragment>
      <Card sx={{ boxShadow: 'none' }}>
        <Grid container spacing={1.5}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" fontWeight={700} color="primary">
            Incomplete Profiles
            </Typography>
          </Grid>
          {
            isHomePage && <Grid container item justifyContent={'flex-end'} size={{ xs: 12, sm: 6 }}>
              <Button variant="outlined" sx={{ width: { xs: '100%', sm: '102px' } }} onClick={() => router.visit('/admin-collab-creators')}>See All</Button>
            </Grid>
          }
        </Grid>

        <Typography fontWeight={500} mb={2}>
          These accounts have started the account creation process but have not complete their profiles and verifications.
        </Typography>
        {
          unverifiedAccounts?.data?.length > 0 ? <GenericMuiDatagrid
            {...unverifiedAccounts}
            hideFooter={isHomePage}
            columns={columns}
            height={isHomePage ? 'auto' : '64vh'}
            setPagination={setPagination}
          >
            {
              unverifiedAccounts?.data?.map((user, index) => {
                return (
                  <Fragment key={`user-key-${index}`} >
                    <Box sx={{ padding: '16px' }}>
                      <Grid container>
                        <Grid container item size={10} alignItems={'center'}>
                          <Grid item size={5}>
                            <GenericAvatar location={user?.details?.profile_picture?.location} avatarId={user?.id} size={'92px'} backgroundColor={'background.default'} />
                          </Grid>
                          <Grid item size={7}>
                            <Stack>
                              {user?.name ? <Typography fontWeight={700}>{user?.name}</Typography> : <Typography color="grey.light">N/A</Typography>}
                              <Typography noWrap>{user?.details?.profession}</Typography>
                              <Typography
                                fontWeight={700}
                                textTransform={'capitalize'}
                                color={'error'}
                              >
                                {user?.status === EMAIL_VERIFIED ? 'Ondato Unverified' : 'Email Unverified'}
                              </Typography>
                              {user?.current_step ? <Typography textTransform={'capitalize'} > {kebabToSentence(user?.current_step)}</Typography> : 'Initial Signup'}
                            </Stack>
                          </Grid>
                        </Grid>
                        <Grid container item justifyContent={'flex-end'} size={2}>
                          <Typography color="text.light">{formatDate(new Date(user?.created_at), DAY_ONLY_FORMAT)}</Typography>
                        </Grid>
                      </Grid>
                      <Stack direction={'row'} gap={0.5} mt={1.5}>
                        <Button fullWidth variant="contained" color="error" onClick={() => handleOpenDialog('Declining', user, DECLINE_CHECKLIST, DECLINE)}>Decline</Button>
                        {user.current_step && <Button fullWidth variant="contained" color={'warning'} onClick={() => handleAcceptChecker(user)}>Accept</Button>}
                      </Stack>
                      <Stack direction={'column'} mt={1.5} gap={1.5}>
                        <Button fullWidth variant="contained" onClick={() => handleOpenProfileDetailsDialog(user?.id)}>Details</Button>
                      </Stack>
                    </Box>
                    <Divider sx={{ borderColor: 'borders.main' }} />
                  </Fragment>
                );
              })
            }
          </GenericMuiDatagrid> : <GenericNoData icon={<UsersThree size={40} />} height={'67vh'} label={'No incoming approval'} />
        }
      </Card>
      <ReasonDialog
        details={details}
        setDetails={setDetails}
        dialogDetails={dialogDetails}
        openDialog={openReasonDialog}
        handleRemoveFromList={handleRemoveFromList}
        handleCloseDialog={() => handleCloseReasonDialog()}
      />
      <ProfileDetails
        isBypassOndato={true}
        openDialog={openProfileDetailsDialog}
        userId={userId}
        handleClose={handleCloseProfileDetailsDialog}
        handleOpenReasonDialog={handleOpenDialog}
        handleAcceptUser={handleAcceptChecker}
        refetchCreatorList={() => fetchUnverifiedAccounts()}
      />
      <GenericDialog
        title={'Bypass Verification'}
        width={'550px'}
        openDialog={acceptConfirmationState}
        handleClose={() => setAcceptConfirmationState(false)}
      >
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} mb={2}>
          <GenericCircleIcon color={'primary.main'}>
            <Warning size={96} weight="thin" />
          </GenericCircleIcon>
        </Box>
        <Stack gap={3} alignItems={'center'}>
          <Typography variant="subtitle1" textAlign={'center'}>
            You are about to accept a user <b>{selectedCreator?.status === EMAIL_VERIFIED ? 'without Ondato verification.' : 'Ondato Rejected.'}</b>
            <br></br>
            Are you sure you want to continue?
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} mt={2}>
          <Button variant="outlined" sx={{ width: { xs: '100%', sm: '250px' } }} onClick={() => setAcceptConfirmationState(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            sx={{ width: { xs: '100%', sm: '250px' } }}
            onClick={() => { handleAcceptUser(selectedCreator); setSelectedCreator(null); setAcceptConfirmationState(false); }}
          >
            Approve Anyway
          </Button>
        </Stack>
      </GenericDialog>
    </Fragment >
  );
};